import { useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";

// Shape convention for the workspace_setting rows the client cares about.
// The store itself is opaque (TEXT value); JSON blobs live as stringified JSON.
export type AgencyContentSettings = {
  display_name?: string;
  entity_label?: string;
  entity_label_plural?: string;
  sms_signature?: string;
  mrr_goal_cents?: string; // integer string
  mrr_goal_deadline?: string; // ISO date
  day_one?: string; // ISO date
  one_thing?: string;
  service_pitch?: string; // what the agency sells, e.g. "Voice AI + GBP optimization" — feeds drill prompts
  gates?: string; // JSON array
  clocks?: string; // JSON array
  enabled_modules?: string; // JSON array of module IDs; unset = all enabled
  trial_price_cents?: string;
  full_price_cents?: string;
  close_rate_target_pct?: string; // your close-rate plan, e.g. "50" — replaces the old hardcoded 50%
  show_rate_target_pct?: string;  // your show-rate target, e.g. "80"
  quick_actions?: string; // JSON array of { label, module } — the Today "Quick actions" rail
  research_web_searches?: string; // pre-call research: max web searches (1-10)
  research_web_fetches?: string;  // pre-call research: max page fetches (1-6)
  research_timeout_s?: string;    // pre-call research: time budget in seconds (30-280)
  research_model?: string;        // "sonnet" | "opus"
  call_length_min?: string; // Live Call cockpit target call length (minutes)
  demo_number?: string; // demo phone line shown in objection flyouts
  four_things?: string; // JSON array of { key, short, full } — the cockpit scorecard
  brand_color?: string; // hex — rebrands --primary and friends per agency
  hide_empty_fields?: string; // "1" hides empty GHL fields on the account detail
  permission_overrides?: string; // JSON PermissionOverrides — owner-edited diffs over the role matrix
};

// The agency's full monthly price in DOLLARS (for ROI math). 0 = unset.
export function monthlyPriceFrom(settings: AgencyContentSettings | null): number {
  const n = settings?.full_price_cents ? parseInt(settings.full_price_cents, 10) : NaN;
  return Number.isFinite(n) && n > 0 ? n / 100 : 0;
}

// Parse enabled_modules; returns null when unset (meaning "all enabled").
export function parseEnabledModules(s?: string): string[] | null {
  if (!s) return null;
  try { const v = JSON.parse(s); return Array.isArray(v) ? v.map(String) : null; } catch { return null; }
}

type SettingsResp = { settings: AgencyContentSettings };

const KEY = ["/api/agency/settings"];

export function useAgencySettings() {
  const qc = useQueryClient();
  const q = useQuery<SettingsResp>({
    queryKey: KEY,
    queryFn: () => api.get<SettingsResp>("/api/agency/settings"),
  });
  const m = useMutation({
    mutationFn: (patch: AgencyContentSettings) =>
      api.put<SettingsResp>("/api/agency/settings", patch),
    onSuccess: (data) => qc.setQueryData(KEY, data),
  });
  const update = useCallback((patch: AgencyContentSettings) => m.mutateAsync(patch), [m]);
  return {
    settings: q.data?.settings ?? null,
    loading: q.isLoading,
    saving: m.isPending,
    update,
    reload: () => q.refetch(),
  };
}

// Helpers for the JSON-encoded fields.
export function parseGates(s?: string): Array<{ n: number; label: string; status: "blocked" | "progress" | "open"; note: string }> {
  if (!s) return [];
  try { const v = JSON.parse(s); return Array.isArray(v) ? v : []; } catch { return []; }
}
export function parseClocks(s?: string): Array<{ name: string; eta: string; note: string }> {
  if (!s) return [];
  try { const v = JSON.parse(s); return Array.isArray(v) ? v : []; } catch { return []; }
}
// The Today "Quick actions" rail. Each item links to a module by id.
// Unset falls back to the two universal defaults so the rail is never empty.
export type QuickAction = { label: string; module: string };
export const DEFAULT_QUICK_ACTIONS: QuickAction[] = [
  { label: "Warm-up drill", module: "drill-range" },
  { label: "Missed-call math", module: "missed-call-math" },
];
export function parseQuickActions(s?: string): QuickAction[] {
  if (!s) return DEFAULT_QUICK_ACTIONS;
  try {
    const v = JSON.parse(s);
    if (!Array.isArray(v)) return DEFAULT_QUICK_ACTIONS;
    const clean = v
      .filter((x) => x && typeof x.label === "string" && typeof x.module === "string")
      .map((x) => ({ label: String(x.label), module: String(x.module) }));
    return clean;
  } catch {
    return DEFAULT_QUICK_ACTIONS;
  }
}

export function goalCentsFrom(settings: AgencyContentSettings | null): number {
  const n = settings?.mrr_goal_cents ? parseInt(settings.mrr_goal_cents, 10) : NaN;
  // 0 = "no goal set" — callers render a set-your-goal nudge instead of
  // pretending some other agency's target belongs to this one.
  return Number.isFinite(n) && n > 0 ? n : 0;
}
